import React, { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "../ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { Badge } from "../ui/badge";
import ChatTab from "./ChatTab";
import AuditTab from "./AuditTab";
import ChangesTab from "./ChangesTab";
import timeTrackingService from "../../services/timeTrackingService";
import {
  Zap,
  LogOut,
  MessageSquare,
  ShieldCheck,
  History,
  Moon,
  Sun,
  FolderOpen,
  Upload,
  Loader2,
  Check,
  ChevronDown,
  User,
  Clock,
  Pause,
  Play,
  ExternalLink,
  Link,
  Unlink,
  FileText,
  GitBranch,
  Save,
  Send,
  Lock,
  Globe
} from "lucide-react";

// Language translations
const translations = {
  nb: {
    selectCase: "Velg sak...",
    logout: "Logg ut",
    darkMode: "Mørk modus",
    lightMode: "Lys modus",
    paused: "Pauset",
    clickToContinue: "Klikk for å fortsette",
    clickToPause: "Klikk for å pause",
    linkedToLexy: "Koblet til Lexy",
    version: "Versjon",
    chat: "Chat",
    audit: "Gransking",
    changes: "Endringer",
    saveToLexy: "Lagre til Lexy",
    saving: "Lagrer...",
    saved: "Lagret!",
    openInLexy: "Åpne i Lexy",
    publishToClient: "Publiser til klient",
    publishing: "Publiserer...",
    markFinal: "Marker som final",
    confirmMarkFinal: "Markere dokumentet som final? Det vil bli låst og kan ikke endres."
  },
  en: {
    selectCase: "Select case...",
    logout: "Log out",
    darkMode: "Dark mode",
    lightMode: "Light mode",
    paused: "Paused",
    clickToContinue: "Click to continue",
    clickToPause: "Click to pause",
    linkedToLexy: "Linked to Lexy",
    version: "Version",
    chat: "Chat",
    audit: "Audit",
    changes: "Changes",
    saveToLexy: "Save to Lexy",
    saving: "Saving...",
    saved: "Saved!",
    openInLexy: "Open in Lexy",
    publishToClient: "Publish to client",
    publishing: "Publishing...",
    markFinal: "Mark as final",
    confirmMarkFinal: "Mark document as final? It will be locked and cannot be changed."
  }
};

// Connection Banner Component
const LexyConnectionBanner = ({ lexyMetadata, selectedCase, cases, t }) => {
  if (!lexyMetadata?.documentId) {
    return null;
  }
  
  // Find case name from cases list
  const linkedCase = cases?.find(c => c.id === lexyMetadata.caseId);
  const caseName = linkedCase?.title || linkedCase?.topic || 'Ukjent sak';
  
  return (
    <div className="mx-4 mb-3 p-3 bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 rounded-xl">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-6 h-6 rounded-full bg-violet-100 flex items-center justify-center">
          <Link className="w-3.5 h-3.5 text-violet-600" />
        </div>
        <span className="text-sm font-medium text-violet-800">
          {t.linkedToLexy}
        </span>
      </div>
      <div className="ml-8 space-y-0.5">
        <p className="text-xs text-violet-700 truncate">
          📁 {caseName}
        </p>
        <div className="flex items-center gap-2 text-xs text-violet-600">
          <span className="flex items-center gap-1">
            <GitBranch className="w-3 h-3" />
            {t.version} {lexyMetadata.version}
          </span>
          {lexyMetadata.documentName && (
            <>
              <span>•</span>
              <span className="flex items-center gap-1 truncate max-w-[150px]">
                <FileText className="w-3 h-3" />
                {lexyMetadata.documentName}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export const TaskPane = ({
  user,
  cases,
  selectedCase,
  onSelectCase,
  scope,
  selectedText,
  onScopeChange,
  chatHistory,
  onSendMessage,
  onApply,
  onAudit,
  changes,
  loading,
  onLogout,
  token,
  isOfficeMode,
  documentName,
  onSaveWorkSession,
  onPublishToClient,
  onMarkFinal,
  workSessionStats,
  lastSaveResult,
  onOpenInLexy,
  lexyMetadata,
  onReviewDocument,
  documentSuggestions = [],
  onDismissSuggestion,
  onOpenSection
}) => {
  const [activeTab, setActiveTab] = useState("chat");
  const [theme, setTheme] = useState("light");
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isMarkingFinal, setIsMarkingFinal] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState(false);
  const [language, setLanguage] = useState<'nb' | 'en'>(() => {
    return (localStorage.getItem('lexy_plugin_lang') as 'nb' | 'en') || 'nb';
  });
  
  const t = translations[language];
  
  const handleLanguageChange = (lang: 'nb' | 'en') => {
    setLanguage(lang);
    localStorage.setItem('lexy_plugin_lang', lang);
  };
  
  // Determine if this is a version update or new document
  const hasLexyConnection = lexyMetadata?.documentId;
  const isVersionUpdate = hasLexyConnection && lexyMetadata?.caseId;
  
  // Time tracking state
  const [timeState, setTimeState] = useState({
    isTracking: false,
    isPaused: false,
    totalSeconds: 0,
    formattedTime: "0:00"
  });

  // Auto-select case if we have Lexy metadata
  useEffect(() => {
    if (lexyMetadata?.caseId && cases?.length > 0 && !selectedCase) {
      const linkedCase = cases.find(c => c.id === lexyMetadata.caseId);
      if (linkedCase) {
        onSelectCase(linkedCase.id);
      }
    }
  }, [lexyMetadata, cases, selectedCase, onSelectCase]);

  // Start time tracking when component mounts
  useEffect(() => {
    // Start tracking
    timeTrackingService.start();
    
    // Listen for updates
    const unsubscribe = timeTrackingService.addListener((state) => {
      setTimeState(state);
    });
    
    // Cleanup on unmount
    return () => {
      unsubscribe();
      // Don't stop tracking on unmount - keep it running
    };
  }, []);

  const handleSaveToLexy = async () => {
    if (!selectedCase || !onSaveWorkSession) return;
    setIsSaving(true);
    setSaveSuccess(false);
    try {
      await onSaveWorkSession();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 5000); // Longer timeout to allow "Open in Lexy"
    } catch (error) {
      console.error('Save failed:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublishToClient = async () => {
    if (!selectedCase || !onPublishToClient) return;
    setIsPublishing(true);
    setPublishSuccess(false);
    try {
      // First save the document
      await onSaveWorkSession();
      // Then publish it
      await onPublishToClient();
      setPublishSuccess(true);
      setTimeout(() => setPublishSuccess(false), 3000);
    } catch (error) {
      console.error('Publish failed:', error);
    } finally {
      setIsPublishing(false);
    }
  };

  const handleMarkFinal = async () => {
    if (!selectedCase || !onMarkFinal) return;
    if (!window.confirm('Markere dokumentet som final? Det vil bli låst og kan ikke endres.')) return;
    setIsMarkingFinal(true);
    try {
      // First save the document
      await onSaveWorkSession();
      // Then mark as final
      await onMarkFinal();
    } catch (error) {
      console.error('Mark final failed:', error);
    } finally {
      setIsMarkingFinal(false);
    }
  };

  const handleOpenInLexy = () => {
    if (onOpenInLexy && lastSaveResult?.lexyUrl) {
      onOpenInLexy(lastSaveResult.lexyUrl);
    }
  };

  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    document.documentElement.classList.toggle("dark", newTheme === "dark");
  };

  const toggleTimeTracking = () => {
    timeTrackingService.toggle();
  };

  return (
    <TooltipProvider>
      <aside
        className={`bg-white border-l border-slate-200 flex flex-col h-screen overflow-hidden ${
          isOfficeMode ? 'w-full' : 'w-full lg:w-[400px]'
        }`}
        data-testid="taskpane-container"
      >
        {/* Header - Clean white */}
        <header className="sticky top-0 z-20 bg-white border-b border-slate-100">
          {/* Top row */}
          <div className="h-14 px-4 flex items-center justify-between">
            {/* Logo - Violet gradient like dashboard */}
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/20">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-slate-900">Lexy</span>
            </div>

            {/* Right side controls */}
            <div className="flex items-center gap-1">
              {/* Time tracker */}
              <div
                role="button"
                tabIndex={0}
                className={`h-8 px-3 gap-1.5 font-mono text-xs inline-flex items-center rounded-lg cursor-pointer transition-all duration-200 select-none ${
                  timeState.isPaused 
                    ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' 
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleTimeTracking();
                }}
                onMouseDown={(e) => e.preventDefault()}
                onTouchStart={() => toggleTimeTracking()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleTimeTracking();
                  }
                }}
                data-testid="time-tracker"
                title={timeState.isPaused ? t.clickToContinue : t.clickToPause}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {timeState.isPaused ? (
                  <Play className="w-3.5 h-3.5" />
                ) : (
                  <Clock className={`w-3.5 h-3.5 ${timeState.isTracking ? 'text-violet-600' : ''}`} />
                )}
                <span>{timeState.formattedTime}</span>
                {timeState.isPaused && (
                  <span className="text-[10px] ml-1 opacity-70">({t.paused})</span>
                )}
              </div>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8 text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                    onClick={toggleTheme}
                    data-testid="theme-toggle"
                  >
                    {theme === "light" ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{theme === "light" ? t.darkMode : t.lightMode}</TooltipContent>
              </Tooltip>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 px-2 gap-1.5 hover:bg-slate-100">
                    <div className="w-6 h-6 rounded-full bg-violet-100 flex items-center justify-center">
                      <User className="w-3.5 h-3.5 text-violet-600" />
                    </div>
                    <ChevronDown className="w-3 h-3 text-slate-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium truncate text-slate-900">{user?.name}</p>
                    <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  
                  {/* Language selector in dropdown */}
                  <div className="px-3 py-2">
                    <p className="text-xs text-slate-500 mb-2 flex items-center gap-1.5">
                      <Globe className="w-3.5 h-3.5" />
                      {language === 'nb' ? 'Språk' : 'Language'}
                    </p>
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleLanguageChange('nb')}
                        className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                          language === 'nb' 
                            ? 'bg-slate-900 text-white' 
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        🇳🇴 Norsk
                      </button>
                      <button
                        onClick={() => handleLanguageChange('en')}
                        className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                          language === 'en' 
                            ? 'bg-slate-900 text-white' 
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        🇬🇧 English
                      </button>
                    </div>
                  </div>
                  
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onLogout} className="text-red-600 focus:text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    {t.logout}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Case selector row */}
          <div className="px-4 pb-3 space-y-2">
            {/* Lexy Connection Banner */}
            <LexyConnectionBanner 
              lexyMetadata={lexyMetadata} 
              selectedCase={selectedCase}
              cases={cases}
              t={t}
            />
            
            {/* Case selector - hide if already connected via Lexy */}
            {!hasLexyConnection && (
              <Select value={selectedCase?.id || ""} onValueChange={onSelectCase}>
                <SelectTrigger 
                  className="h-10 bg-slate-50 border-slate-200 hover:bg-slate-100 transition-colors rounded-xl" 
                  data-testid="case-selector"
                >
                  <FolderOpen className="w-4 h-4 mr-2 text-slate-400 shrink-0" />
                  <SelectValue placeholder={t.selectCase} />
                </SelectTrigger>
                <SelectContent>
                  {cases.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      <span className="truncate">{c.title || c.topic || 'Uten navn'}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Main action button - Smart based on Lexy connection */}
            {(selectedCase || hasLexyConnection) && (
              <div className="space-y-2">
                {/* Stats preview */}
                {workSessionStats && (
                  <div className="flex items-center justify-between text-xs text-slate-500 px-1">
                    <span>📝 {workSessionStats.changesCount || 0} {language === 'nb' ? 'endringer' : 'changes'}</span>
                    <span>💬 {workSessionStats.commentsCount || 0} {language === 'nb' ? 'kommentarer' : 'comments'}</span>
                    <span>⏱️ {timeState.formattedTime}</span>
                  </div>
                )}

                {/* Save to Lexy button */}
                <div className="grid grid-cols-1 gap-2">
                  <Button
                    className={`w-full h-12 transition-all duration-200 font-medium text-base rounded-xl ${
                      saveSuccess 
                        ? 'bg-emerald-600 hover:bg-emerald-700' 
                        : 'bg-slate-900 hover:bg-slate-800'
                    }`}
                    onClick={handleSaveToLexy}
                    disabled={isSaving}
                    data-testid="save-work-session-button"
                  >
                    {isSaving ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" />{t.saving}</>
                    ) : saveSuccess ? (
                      <><Check className="w-5 h-5 mr-2" />
                        {isVersionUpdate ? `${t.saved} v${(lexyMetadata?.version || 0) + 1}` : t.saved}
                      </>
                    ) : (
                      <><Upload className="w-5 h-5 mr-2" />{t.saveToLexy}</>
                    )}
                  </Button>
                  
                  {/* Version note */}
                  {saveSuccess && (
                    <p className="text-xs text-slate-400 text-center mt-2">
                      {language === 'nb' 
                        ? 'Endringer lagret. Fortsett i Lexy for å dele, publisere eller arkivere.'
                        : 'Changes saved. Continue in Lexy to share, publish or archive.'}
                    </p>
                  )}
                </div>

                {/* Open in Lexy button - shows after successful save */}
                {saveSuccess && lastSaveResult?.lexyUrl && (
                  <Button
                    variant="outline"
                    className="w-full h-10"
                    onClick={handleOpenInLexy}
                    data-testid="open-in-lexy-button"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Åpne saken i Lexy
                  </Button>
                )}
              </div>
            )}
          </div>
        </header>

        {/* Tabs */}
        <Tabs 
          value={activeTab} 
          onValueChange={setActiveTab}
          className="flex-1 flex flex-col min-h-0"
          data-testid="main-tabs"
        >
          <TabsList className="h-11 px-4 bg-slate-50 border-b border-slate-100 rounded-none justify-start gap-1">
            <TabsTrigger 
              value="chat" 
              className="data-[state=active]:bg-white data-[state=active]:text-violet-600 data-[state=active]:shadow-sm rounded-lg px-3 gap-1.5 text-slate-600"
              data-testid="tab-chat"
            >
              <MessageSquare className="w-4 h-4" />
              {t.chat}
            </TabsTrigger>
            <TabsTrigger 
              value="audit" 
              className="data-[state=active]:bg-white data-[state=active]:text-violet-600 data-[state=active]:shadow-sm rounded-lg px-3 gap-1.5 text-slate-600"
              data-testid="tab-audit"
            >
              <ShieldCheck className="w-4 h-4" />
              {t.audit}
            </TabsTrigger>
            <TabsTrigger 
              value="changes" 
              className="data-[state=active]:bg-white data-[state=active]:text-violet-600 data-[state=active]:shadow-sm rounded-lg px-3 gap-1.5 text-slate-600"
              data-testid="tab-changes"
            >
              <History className="w-4 h-4" />
              {t.changes}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="chat" className="flex-1 mt-0 min-h-0 data-[state=inactive]:hidden bg-white">
            <ChatTab
              chatHistory={chatHistory}
              onSendMessage={onSendMessage}
              onApply={onApply}
              loading={loading}
              selectedText={selectedText}
              scope={scope}
              language={language}
              documentName={documentName}
              userName={user?.name}
              onReviewDocument={onReviewDocument}
              documentSuggestions={documentSuggestions}
              onDismissSuggestion={onDismissSuggestion}
              onOpenSection={onOpenSection}
            />
          </TabsContent>

          <TabsContent value="audit" className="flex-1 mt-0 min-h-0 data-[state=inactive]:hidden overflow-y-auto bg-white">
            <AuditTab
              onAudit={onAudit}
              onApply={onApply}
              loading={loading}
              token={token}
              language={language}
            />
          </TabsContent>

          <TabsContent value="changes" className="flex-1 mt-0 min-h-0 data-[state=inactive]:hidden overflow-y-auto bg-white">
            <ChangesTab changes={changes} language={language} />
          </TabsContent>
        </Tabs>
      </aside>
    </TooltipProvider>
  );
};
export default TaskPane;
